// src/pages/About.jsx
import React, { useEffect, useRef } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import living from "../Assets/about/about-trilium-banner.webp";
import team1 from "../Assets/Media/why-chs-townbell.jpg";
import team2 from "../Assets/about/plac-holder-img.webp";
import backgroundLines from "../Assets/about/background-lines.svg";
import aboutIcon1 from "../Assets/about/award-winner-img.webp";
import aboutIcon2 from "../Assets/about/award-winner.webp";
import aboutIcon3 from "../Assets/about/award-winner-2.webp";
import { motion } from "framer-motion";
import {
  FaPlus,
  FaBullseye,
  FaHandshake,
  FaLightbulb,
  FaUsers,
  FaSearch,
} from "react-icons/fa";
import {
  FiCheckCircle,
  FiClock,
  FiHeart,
  FiMapPin,
  FiMaximize,
  FiUsers,
} from "react-icons/fi";

gsap.registerPlugin(ScrollTrigger);

const About = () => {
  const heroRef = useRef(null);

  // Initialize animations
  useEffect(() => {
    // Hero section animation
    gsap.from(heroRef.current, {
      opacity: 0,
      y: 100,
      duration: 1,
      scrollTrigger: {
        trigger: heroRef.current,
        start: "top bottom",
        toggleActions: "play none none none",
      },
    });

    // Section content fade-up animations
    gsap.utils.toArray(".fade-up").forEach((section) => {
      gsap.fromTo(
        section,
        { opacity: 0, y: 50 },
        {
          opacity: 1,
          y: 0,
          duration: 1,
          scrollTrigger: {
            trigger: section,
            start: "top 80%",
          },
        }
      );
    });

    // Counter animations
    const counters = document.querySelectorAll(".count-box .count");
    counters.forEach((counter) => {
      const target = +counter.getAttribute("data-target");
      const count = { value: 0 };

      ScrollTrigger.create({
        trigger: counter,
        start: "top 80%",
        onEnter: () => {
          gsap.to(count, {
            value: target,
            duration: 2,
            onUpdate: () => {
              counter.textContent = Math.ceil(count.value);
            },
          });
        },
      });
    });

    // Clean up
    return () => {
      ScrollTrigger.getAll().forEach((trigger) => trigger.kill());
    };
  }, []);

  // -------------------------grok----------------------------------------
  useEffect(() => {
    // GSAP animations for the About section
    gsap.fromTo(
      ".concept-heading h2",
      { opacity: 0, y: 50 },
      {
        opacity: 1,
        y: 0,
        duration: 1,
        scrollTrigger: {
          trigger: "#about",
          start: "top 80%",
          end: "top 20%",
          scrub: 1,
        },
      }
    );

    gsap.fromTo(
      ".hover-btn",
      { scale: 0.8, opacity: 0 },
      {
        scale: 1,
        opacity: 1,
        duration: 0.8,
        delay: 0.2,
        scrollTrigger: {
          trigger: "#about",
          start: "top 80%",
          end: "top 20%",
          scrub: 1,
        },
      }
    );

    gsap.fromTo(
      ".about-content > *",
      { opacity: 0, x: 50 },
      {
        opacity: 1,
        x: 0,
        duration: 1,
        stagger: 0.2,
        scrollTrigger: {
          trigger: ".about-content",
          start: "top 80%",
          end: "top 20%",
          scrub: 1,
        },
      }
    );

    gsap.fromTo(
      ".count-box",
      { opacity: 1, scale: 0.8 },
      {
        opacity: 1,
        scale: 1,
        duration: 1,
        stagger: 0.3,
        scrollTrigger: {
          trigger: ".counts",
          start: "top 80%",
          end: "top 20%",
          scrub: 1,
        },
      }
    );

    gsap.from(".why-us-item", {
      opacity: 0,
      y: 50,
      duration: 1,
      stagger: 0.2,
      ease: "power2.out",
    });

    gsap.from(".why-us-item", {
      opacity: 0,
      y: 50,
      duration: 1,
      stagger: 0.2,
      ease: "power2.out",
    });

    gsap.from(".more-about-item", {
      opacity: 0,
      y: 50,
      duration: 1,
      stagger: 0.2,
      ease: "power2.out",
    });

    gsap.from(".value-card", {
      opacity: 0,
      y: 50,
      duration: 1,
      stagger: 0.2,
      ease: "power2.out",
    });
  }, []);

  const teamMembers = [
    {
      name: "Deepak Garg",
      role: "Director",
      desc:"Bringing 7+ years of expertise in real estate with a BBA background.Skilled in property management, client relations, and market growth."
    },
    {
      name: "Tanish Garg",
      role: "Director",
       desc:"Real estate specialist with 5+ years’ experience, backed by BBA & MBA.Focused on delivering strategic solutions and driving business success."
    },
   
  ];
  const features = [
    {
      icon: <FaPlus className="text-white-500 text-2xl mb-4" />,
      title: "Future-Ready Design",
      desc: "Our developments are built with evolving urban lifestyles in mind.",
    },
    {
      icon: <FaBullseye className="text-white-500 text-2xl mb-4" />,
      title: "Advanced Construction",
      desc: "Using modern technologies like Mivan formwork for stronger, safer structures.",
    },
    {
      icon: <FaHandshake className="text-white-500 text-2xl mb-4" />,
      title: "Essential Conveniences On-Site",
      desc: "Retail, parking, and basic services are all integrated into our community planning.",
    },
    {
      icon: <FaLightbulb className="text-white-500 text-2xl mb-4" />,
      title: "Uninterrupted Utilities",
      desc: "24/7 power backup and water supply make life seamless.",
    },
    {
      icon: <FaUsers className="text-white-500 text-2xl mb-4" />,
      title: "Peaceful Surroundings",
      desc: "Enjoy the benefits of urban access without sacrificing natural calm.",
    },
    {
      icon: <FaSearch className="text-white-500 text-2xl mb-4" />,
      title: "Value-Driven Living",
      desc: "Whether for personal use or investment, every square foot is designed for long-term value.",
    },
  ];

  const cardVariants = {
    initial: { rotate: 0 },
    hover: {
      rotate: 360,
      transition: { duration: 0.6, ease: "linear" },
    },
  };
  const iconVariants = {
    initial: { rotate: 0 },
    hover: {
      rotate: 360,
      transition: { duration: 1, ease: "linear", repeat: Infinity },
    },
  };

  const valueVariants = {
    hiddenLeft: { opacity: 0, x: -80 },
    hiddenRight: { opacity: 0, x: 80 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.6, ease: "easeOut" },
    },
  };

  const whyFeatures = [
    {
      icon: <FiMapPin />,
      title: "Prime Locations",
      desc: "Our projects are carefully placed in rapidly developing, well-connected areas with high future potential.",
    },
    {
      icon: <FiMaximize />,
      title: "Smart & Spacious Layouts",
      desc: "We design homes that maximize natural light, ventilation, and liveable space for real everyday needs.",
    },
    {
      icon: <FiHeart />,
      title: "Amenities That Matter",
      desc: "From lifestyle clubs and fitness zones to kids' play areas and wellness spaces—we build more than just walls.",
    },
    {
      icon: <FiClock />,
      title: "On-Time Delivery",
      desc: "Our team is committed to timelines, ensuring peace of mind and financial reliability for every buyer.",
    },
    {
      icon: <FiCheckCircle />,
      title: "Transparent Dealings",
      desc: "No hidden clauses. No last-minute surprises. Our communication and pricing are always clear and honest.",
    },
    {
      icon: <FiUsers />,
      title: "Built for Community Living",
      desc: "Each project is designed to foster a sense of belonging with green areas, shared spaces, and secure environments.",
    },
  ];
  const values = [
    {
      number: "01",
      title: "Creativity",
      desc: "We approach each project with fresh ideas, design innovation, and a deep understanding of how people live, work, and connect.",
      align: "right",
    },
    {
      number: "02",
      title: "Quality",
      desc: "We’re committed to high standards in every brick laid—from materials to execution—ensuring durability, elegance, and performance.",
      align: "left",
    },
    {
      number: "03",
      title: "Collaboration",
      desc: "At Townbell, we believe success is shared. We work closely with customers, partners, and communities to build with purpose and unity.",
      align: "right",
    },
    {
      number: "04",
      title: "Integrity",
      desc: "Transparency and trust are the cornerstones of our reputation. We do what we say and deliver what we promise—every time.",
      align: "left",
    },
  ];
  return (
    <div className="min-h-screen">
      {/* Hero Banner */}
      <section
        ref={heroRef}
        className="main-home-section relative min-h-screen flex items-end justify-start overflow-hidden"
        style={{
          backgroundImage: `url(${living})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          overflow: "hidden",
        }}
      >
        <div className="h-screen flex flex-col justify-end item-center text-center z-10 px-4">
          <h1
            className="md:text-7xl text-5xl font-bold text-white mb-8"
            style={{ textShadow: "2px 2px 4px rgba(0,0,0,0.8)" }}
          >
            About Us
          </h1>
        </div>
      </section>

      {/* Section 1: About Our Agency-------------------------white */}
      <div
        id="about"
        className="w-full flex justify-center relative lg:py-10 py-5  "
      >
        <div className="w-[95%] max-w-[100vw] flex flex-col lg:flex-row items-center lg:items-start z-37  lg:gap-0 gap-4">
          <div className="concept-heading h-full lg:w-[35%] w-[100%] flex flex-col md:flex-row lg:flex-col justify-between items-center md:items-start relative">
            <h2 className="h2 capitalize 2xl:text-[55px] xl:text-[48px] lg:text-[46px] md:text-[40px] text-[30px] font-bold leading-[110%] lg:sticky top-[50px] lg:mb-[100px] mb-2 w-full">
              Crafting Landmarks,
              <br className="hidden lg:block" /> Building Trust
            </h2>
          </div>

          <div className="about-content lg:w-[65%] w-[100%] flex flex-col gap-[15px] lg:gap-[20px]">
            <h4 className="text-[16px] font-normal text-[#000000] leading-[24px] ">
              Welcome to Townbell India Landcraft Private Limited— where vision
              meets craftsmanship and every project reflects our commitment to
              thoughtful, future-ready living. As one of the emerging names in
              North India’s real estate space, we pride ourselves on delivering
              premium developments that blend smart planning, quality
              construction, and lifestyle-rich amenities.
            </h4>
            <h4 className="text-[16px] text-[#000000] font-normal leading-[24px]">
              From vibrant residential communities to integrated townships and
              commercial spaces, our focus remains on creating environments that
              enhance life—not just house it. Strategically located projects,
              efficient layouts, modern infrastructure, and timely delivery form
              the foundation of every Townbell development. Whether you’re
              buying your first home or expanding your portfolio, you can trust
              us to deliver long-term value with a personal touch.
            </h4>
            <div className="md:max-w-xl w-full flex items-center justify-between">
              <div>
                <img
                  src={aboutIcon1}
                  alt="Team Member"
                  loading="lazy"
                  className="md:w-[9rem] w-[4rem] h-auto rounded-full mx-auto"
                />
                <p className="font-bold md:text-[16px] text[12px] text-center">
                  Visionary
                  <br />
                  Creations Award
                </p>
              </div>
              <div>
                <img
                  src={aboutIcon2}
                  alt="Team Member"
                  loading="lazy"
                  className="md:w-[9rem] w-[4rem] h-auto rounded-full mx-auto"
                />
                <p className="font-bold md:text-[16px] text[12px] text-center">
                  CineMasters
                  <br />
                  Excellence
                </p>
              </div>
              <div>
                <img
                  src={aboutIcon3}
                  alt="Team Member"
                  loading="lazy"
                  className="md:w-[9rem] w-[4rem] h-auto rounded-full mx-auto"
                />
                <p className="font-bold md:text-[16px] text[12px] text-center">
                  ReelGenius
                  <br />
                  Award
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Section 2: Our values-----------------------black */}
      <div
        className="bg-[#521a27] text-white min-h-screen py-11 px-4"
        style={{
          backgroundImage: `url(${backgroundLines})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <h2 className="w-full capitalize 2xl:text-[55px] xl:text-[48px] lg:text-[46px] md:text-[40px] text-[30px] font-bold text-center mb-8 b-2 mx-auto leading-[110%]">
          Our Core Values <br className="hidden lg:block" />
          The Heart of Every Townbell Project
        </h2>

        <div className="grid md:grid-cols-1 grid-cols-1 gap-8 max-w-5xl mx-auto">
          {values.map((item, index) => (
            <motion.div
              key={index}
              variants={valueVariants}
              initial={item.align === "right" ? "hiddenLeft" : "hiddenRight"}
              whileInView="visible"
              viewport={{ once: true, amount: 0.3 }}
              whileHover={{ scale: 1.03, transition: { duration: 0.2 } }}
              className={`value-card flex mb-8 ${
                item.align === "right"
                  ? "lg:mr-[260px] md:mr-[200px] mr-[10px]"
                  : "lg:ml-[260px] md:ml-[200px] ml-[0px]"
              }`}
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.5 }}
                whileInView={{
                  opacity: 1,
                  scale: 1,
                  transition: { duration: 0.4 },
                }}
                viewport={{ once: true }}
                className="md:text-[105px] text-[70px] font-bold mr-4 text-white-400"
              >
                {item.number}
              </motion.div>
              <div>
                <h4 className="2xl:text-[40px] lg:text-[35px] text-[30px] font-semibold mb-2">
                  {item.title}
                </h4>
                <h5 className="text-white-400 text-[18px]">{item.desc}</h5>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Section 3:Our Fantastic Team-----------------------white */}
      <section className="md:py-12 py-5 px-4">
        <div className="ml-4">
          <h2 className="h2 2xl:text-[55px] xl:text-[48px] lg:text-[46px] md:text-[40px] text-[30px] font-bold mb-8 capitalize text-center">
            Meet Our Fantastic Team
          </h2>
        </div>
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-8 p-8">
          {teamMembers.map((member, index) => (
            <div
              key={index}
              className="border border-[#727272] p-3 overflow-hidden flex flex-col items-center text-center"
            >
              <div className="pt-4 w-full flex flex-col justify-center">
                <h2 className="h2 capitalize text-[24px] font-bold leading-1.5">
                  {member.name}
                </h2>
                <p className="capitalize text-[16px] text-[#521a27] tracking-wide mt-1 pb-4">
                  {member.role}
                </p>
                <p>
                   {member.desc}
                </p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Section 4 : Why us?  --------------------------black */}
      <div className="bg-[#521a27] text-white md:p-10 p-5 px-4">
        <div className="w-full mx-auto md:p-5 p-3">
          <div class className="flex flex-col md:flex-row justify-between">
            <div className="md:w-1/2 mb-8 md:mb-0">
              <img
                src={team1}
                style={{ backgroundSize: "contain" }}
                alt="Team at work"
                className="w-full h-auto"
                loading="lazy"
                decoding="async"
              />
            </div>
            <div className="md:w-1/2 md:pl-12 pl-0">
              <h2 className="capitalize 2xl:text-[55px] xl:text-[48px] lg:text-[46px] md:text-[40px] text-[30px] font-bold mb-6">
                Why Choose Townbell?
              </h2>

              {whyFeatures.map((item, i) => (
                <motion.div
                  key={i}
                  whileHover="hover"
                  initial="initial"
                  className="mb-6"
                >
                  <b className="flex items-center gap-2 mb-[10px] md:text-2xl text-[20px]">
                    <motion.span
                      variants={iconVariants}
                      className="text-yellow-500"
                    >
                      {item.icon}
                    </motion.span>
                    {item.title}
                  </b>
                  <p className="text-white-400 text-[18px]">{item.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* section 5 */}
      <div className="md:p-12 p-5">
        <div className="w-full mx-auto">
          <h3 className="h2 capitalize 2xl:text-[65px] xl:text-[53px] lg:text-[50px] md:text-[45px] text-[30px]  font-bold text-center mb-8">
            Wait! There’s more...
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {features.map((item, index) => (
              <motion.div
                key={index}
                whileHover="hover"
                initial="initial"
                className="bg-white shadow-md hover:shadow-xl transition-all p-6 flex gap-4 items-start cursor-pointer rounded-xl"
              >
                <motion.div
                  variants={cardVariants}
                  className="text-4xl text-500"
                >
                  {item.icon}
                </motion.div>
                <div>
                  <b className="md:text-2xl text-[20px] font-bold mb-3">
                    {item.title}
                  </b>
                  <p className="text-[18px] text-gray-500">{item.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default About;
